import { Check, Clipboard, Eye, EyeOff, LoaderCircle } from "lucide-react";
import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "destructive";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly busy?: boolean;
  readonly busyLabel?: string;
  readonly variant?: ButtonVariant;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    busy = false,
    busyLabel = "İşleniyor…",
    children,
    className = "",
    disabled,
    type = "button",
    variant = "primary",
    ...props
  },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      aria-busy={busy || undefined}
      className={`button button--${variant} ${className}`.trim()}
      disabled={disabled === true || busy}
      type={type}
    >
      {busy ? <LoaderCircle aria-hidden="true" size={16} strokeWidth={1.75} /> : null}
      {busy ? busyLabel : children}
    </button>
  );
});

export type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly label: string;
  readonly icon: ReactNode;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className = "", icon, label, type = "button", ...props },
  ref,
) {
  return (
    <button
      {...props}
      ref={ref}
      aria-label={label}
      className={`icon-button button--quiet ${className}`.trim()}
      title={label}
      type={type}
    >
      {icon}
    </button>
  );
});

interface FieldBaseProps {
  readonly error?: string | undefined;
  readonly help?: ReactNode;
  readonly label: string;
}

export type TextFieldProps = FieldBaseProps & InputHTMLAttributes<HTMLInputElement>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { error, help, id: providedId, label, required, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const helpId = help === undefined ? undefined : `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy =
    [helpId, error === undefined ? undefined : errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}{" "}
        {required === true ? (
          <span aria-hidden="true" className="muted">
            (zorunlu)
          </span>
        ) : null}
      </label>
      <input
        {...props}
        ref={ref}
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        id={id}
        required={required}
      />
      {help === undefined ? null : (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      )}
      <p aria-live="polite" className="field__error" id={errorId}>
        {error ?? ""}
      </p>
    </div>
  );
});

export type PasswordFieldProps = Omit<TextFieldProps, "type">;

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ error, help, id: providedId, label, required, ...props }, ref) {
    const [visible, setVisible] = useState(false);
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const describedBy =
      [
        help === undefined ? undefined : `${id}-help`,
        error === undefined ? undefined : `${id}-error`,
      ]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <div className="field">
        <label htmlFor={id}>
          {label}{" "}
          {required === true ? (
            <span aria-hidden="true" className="muted">
              (zorunlu)
            </span>
          ) : null}
        </label>
        <div className="cluster" style={{ flexWrap: "nowrap" }}>
          <input
            {...props}
            ref={ref}
            aria-describedby={describedBy}
            aria-invalid={error === undefined ? undefined : true}
            aria-label={label}
            id={id}
            required={required}
            style={{ flex: "1 1 auto", minWidth: 0 }}
            type={visible ? "text" : "password"}
          />
          <IconButton
            aria-pressed={visible}
            icon={
              visible ? (
                <EyeOff aria-hidden="true" size={20} strokeWidth={1.75} />
              ) : (
                <Eye aria-hidden="true" size={20} strokeWidth={1.75} />
              )
            }
            label={visible ? "Gizli değeri gizle" : "Gizli değeri göster"}
            onClick={() => {
              setVisible((current) => !current);
            }}
          />
        </div>
        {help === undefined ? null : (
          <p className="field__help" id={`${id}-help`}>
            {help}
          </p>
        )}
        <p aria-live="polite" className="field__error" id={`${id}-error`}>
          {error ?? ""}
        </p>
      </div>
    );
  },
);

export type SelectFieldProps = FieldBaseProps & SelectHTMLAttributes<HTMLSelectElement>;

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { children, error, help, id: providedId, label, required, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const describedBy =
    [help === undefined ? undefined : `${id}-help`, error === undefined ? undefined : `${id}-error`]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}{" "}
        {required === true ? (
          <span aria-hidden="true" className="muted">
            (zorunlu)
          </span>
        ) : null}
      </label>
      <select
        {...props}
        ref={ref}
        aria-describedby={describedBy}
        aria-invalid={error === undefined ? undefined : true}
        id={id}
        required={required}
      >
        {children}
      </select>
      {help === undefined ? null : (
        <p className="field__help" id={`${id}-help`}>
          {help}
        </p>
      )}
      <p aria-live="polite" className="field__error" id={`${id}-error`}>
        {error ?? ""}
      </p>
    </div>
  );
});

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  readonly label: ReactNode;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { id: providedId, label, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  return (
    <label className="cluster" htmlFor={id} style={{ minHeight: "var(--target-size)" }}>
      <input {...props} ref={ref} id={id} type="checkbox" />
      <span>{label}</span>
    </label>
  );
});

export function CopyButton({
  label = "Kopyala",
  value,
}: {
  readonly label?: string;
  readonly value: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => {
      setCopied(false);
    }, 3000);
  };
  return (
    <span className="cluster">
      <Button
        onClick={() => {
          void copy();
        }}
        variant="secondary"
      >
        {copied ? (
          <Check aria-hidden="true" size={16} strokeWidth={1.75} />
        ) : (
          <Clipboard aria-hidden="true" size={16} strokeWidth={1.75} />
        )}
        {label}
      </Button>
      <span aria-live="polite" className="visually-hidden">
        {copied ? "Panoya kopyalandı" : ""}
      </span>
    </span>
  );
}
